import React, { Suspense, useEffect, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import config from './config/env';
import { Toast } from './components/ui';
import GlobalModal from './components/ui/GlobalModal';
import {
  AuthGuard,
  GuestGuard,
  QuizGuard,
  SetPasswordGuard,
} from './components/auth';
import styles from './App.module.scss';

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home'));
const StartChallenge = lazy(() => import('./pages/StartChallenge'));
const Summary = lazy(() => import('./pages/Summary'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Payment = lazy(() => import('./pages/Payment'));
const SetPassword = lazy(() => import('./pages/SetPassword'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const ChooseYourData = lazy(() => import('./pages/ChooseYourData'));

// Loading component
const PageLoader: React.FC = () => (
  <div className={styles.pageLoader}>
    <div className={styles.spinner} />
  </div>
);

const App: React.FC = () => {
  // Initialize Google Tag Manager
  useEffect(() => {
    TagManager.initialize({ gtmId: config.gtmId });
  }, []);

  // Warn before leaving page with unsaved data
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Check if user has started the quiz
      const hasStartedQuiz = sessionStorage.getItem('reimbursementRef');
      if (hasStartedQuiz) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return (
    <div className={styles.app}>
      <Toast />
      <GlobalModal />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ============================================ */}
          {/* Public Routes - Anyone can access */}
          {/* ============================================ */}
          <Route path="/" element={<Home />} />

          {/* ============================================ */}
          {/* Quiz Routes - Require active session */}
          {/* ============================================ */}
          <Route
            path="/startChallenge"
            element={
              <QuizGuard>
                <StartChallenge />
              </QuizGuard>
            }
          />
          <Route
            path="/chooseData"
            element={
              <QuizGuard>
                <ChooseYourData />
              </QuizGuard>
            }
          />
          <Route
            path="/summary"
            element={
              <QuizGuard>
                <Summary />
              </QuizGuard>
            }
          />

          {/* ============================================ */}
          {/* Guest Routes - Redirect if authenticated */}
          {/* ============================================ */}
          <Route
            path="/login"
            element={
              <GuestGuard>
                <Login />
              </GuestGuard>
            }
          />
          <Route
            path="/register"
            element={
              <GuestGuard>
                <Register />
              </GuestGuard>
            }
          />
          <Route
            path="/forgot-password"
            element={<ForgotPassword />}
          />
          <Route
            path="/reset-password"
            element={
              <GuestGuard>
                <ResetPassword />
              </GuestGuard>
            }
          />

          {/* ============================================ */}
          {/* Payment Routes */}
          {/* ============================================ */}
          <Route
            path="/payment"
            element={
              <QuizGuard>
                <Payment />
              </QuizGuard>
            }
          />
          <Route
            path="/set-password"
            element={
              <SetPasswordGuard>
                <SetPassword />
              </SetPasswordGuard>
            }
          />

          {/* ============================================ */}
          {/* Protected Routes - Require authentication */}
          {/* ============================================ */}
          <Route
            path="/profile"
            element={
              <AuthGuard>
                <Profile />
              </AuthGuard>
            }
          />
          <Route
            path="/settings"
            element={
              <AuthGuard>
                <Settings />
              </AuthGuard>
            }
          />

          {/* ============================================ */}
          {/* Fallback - Redirect unknown routes to home */}
          {/* ============================================ */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
};

export default App;
